import { createRouter, createRoute, createRootRoute, Outlet, redirect } from '@tanstack/react-router'
import { Provider } from 'react-redux'
import { store } from './store'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ErrorBoundary } from './components/common/ErrorBoundary'
import { ParticleNetwork } from './components/ui/particle-network'
import { Card, CardContent } from './components/ui/card'
import { AnimatePresence } from 'framer-motion'
import './i18n'
import DashboardLayout from './pages/dashboard/dashboard-layout'
import { useAccountCheck } from './utils/helpers'
import { useTranslation } from './hooks/useTranslation'

// Import all page components
import AuthPage from './pages/auth/auth-page'
import DashboardPage from './pages/dashboard/dash/dashboard-page'
import MyServicesPage from './pages/dashboard/services/my-services-page'
import BrowseServicesPage from './pages/dashboard/services/browse-services-page'
import ConfirmPurchasePage from './pages/dashboard/services/confirm-purchase-page'
import CompletePendingPaymentPage from './pages/dashboard/funds/payment/complete-pending-payment-page'
import ReferralsPage from './pages/dashboard/referrals/referrals-page'
import WalletPage from './pages/dashboard/wallet/wallet-page'
import { TransactionsList, TransactionDetail } from './pages/dashboard/funds/transactions'
import RegisterPage from './pages/auth/register-page'
import LoginPage from './pages/auth/login-page'
import { CompleteRegisterPage } from './pages/auth/complete-register'
import AffiliationWelcomePage from './pages/dashboard/referrals/affiliate-welcome-page'
import ProfileSettings from './pages/dashboard/profile/profile-page'
import NotificationsPage from './pages/dashboard/notifications/notifications-page'

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
})

// Root route wrapper with providers
const RootComponent = () => {
  const { isChecking } = useAccountCheck()
  const { t } = useTranslation()

  if (isChecking) {
    return (
      <div className="relative min-h-screen w-full flex justify-center items-center px-6 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <ParticleNetwork className="w-full h-full" />
        </div>
        <div className="absolute inset-0 bg-[#080c15]/60 z-[1]" />
        <div className="relative z-10">
          <Card className="p-8 max-w-md w-full bg-[#0a0f1c]/90 border-[#0dcbfc]/20 backdrop-blur-md">
            <CardContent className="p-0">
              <p className="rounded-lg bg-[#080c15]/50 p-4 justify-center flex items-center text-white text-lg font-medium animate-pulse">
                Nexa is {t('common.messages.loading')}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <ErrorBoundary>
      <Provider store={store}>
        <QueryClientProvider client={qc}>
          <ParticleNetwork />
          <AnimatePresence mode="wait" initial={false}>
            <Outlet />
          </AnimatePresence>
        </QueryClientProvider>
      </Provider>
    </ErrorBoundary>
  )
}

const rootRoute = createRootRoute({
  component: RootComponent,
})

// Auth routes
const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth',
  component: AuthPage,
})

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  component: RegisterPage,
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
})

const verifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify',
  component: CompleteRegisterPage,
})

// Dashboard layout route
const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  component: DashboardLayout,
})

// Dashboard child routes
const dashboardIndexRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/dashboard/dash' })
  },
})

const dashboardDashRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/dash',
  component: DashboardPage,
})

const myServicesRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/services/my-services',
  component: MyServicesPage,
})

const browseServicesRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/services/browse',
  component: BrowseServicesPage,
})

const confirmPurchaseRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/services/confirm-purchase',
  component: ConfirmPurchasePage,
})

const completePaymentRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/funds/payment/complete',
  component: CompletePendingPaymentPage,
})

const transactionsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/funds/transactions',
  component: TransactionsList,
})

const transactionDetailRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/funds/transactions/$id',
  component: TransactionDetail,
})

const walletRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/funds/wallet',
  component: WalletPage,
})

const referralsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/referrals',
  component: ReferralsPage,
})

const profileRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/profile',
  component: ProfileSettings,
})

const notificationsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/notifications',
  component: NotificationsPage,
})

// Ref route
const refRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ref',
  component: AffiliationWelcomePage,
})

// Catch-all route
const catchAllRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '*',
  beforeLoad: () => {
    throw redirect({ to: '/dashboard/dash' })
  },
})

// Create the route tree
const routeTree = rootRoute.addChildren([
  authRoute,
  registerRoute,
  loginRoute,
  verifyRoute,
  dashboardRoute.addChildren([
    dashboardIndexRoute,
    dashboardDashRoute,
    myServicesRoute,
    browseServicesRoute,
    confirmPurchaseRoute,
    completePaymentRoute,
    transactionsRoute,
    transactionDetailRoute,
    walletRoute,
    referralsRoute,
    profileRoute,
    notificationsRoute,
  ]),
  refRoute,
  catchAllRoute,
])

// Create the router
export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultStaleTime: 5000,
  defaultNotFoundComponent: () => <div className="min-h-screen flex items-center justify-center text-white">404 - Page Not Found</div>,
})

// Register router types
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
